import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save, Upload, X } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const BrandKit = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [primaryColor, setPrimaryColor] = useState('#667eea');
  const [secondaryColor, setSecondaryColor] = useState('#764ba2');
  const [fontFamily, setFontFamily] = useState('DejaVuSans');

  useEffect(() => {
    loadBrandKit();
  }, []);

  const loadBrandKit = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/brand-kit');
      const kit = response.data;
      
      if (kit.logo_url) {
        setLogoPreview(kit.logo_url);
      }
      setPrimaryColor(kit.primary_color);
      setSecondaryColor(kit.secondary_color);
      setFontFamily(kit.font_family);
    } catch (error) {
      console.error('Error loading brand kit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Datei zu groß. Maximal 5MB erlaubt.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Bitte nur Bilddateien hochladen.');
        return;
      }
      
      setLogoFile(file);
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload to backend
      try {
        const formData = new FormData();
        formData.append('logo', file);
        
        const response = await axiosInstance.post('/brand-kit/upload-logo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Update preview with backend response
        setLogoPreview(response.data.logo_url);
        toast.success('Logo erfolgreich hochgeladen!');
      } catch (error) {
        console.error('Error uploading logo:', error);
        toast.error('Fehler beim Hochladen des Logos.');
      }
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        logo_url: logoPreview || null,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        font_family: fontFamily
      };

      await axiosInstance.post('/brand-kit', data);
      toast.success('Brand-Kit erfolgreich gespeichert!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#A78BFA]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brand-kit-section">
      <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.2)]">
        <CardHeader>
          <CardTitle className="text-2xl">Ihr Brand-Kit</CardTitle>
          <p className="text-sm text-[rgba(245,245,247,0.7)] mt-2">
            Speichern Sie Ihr Logo, Farben und Schriftart. Diese werden automatisch bei jeder Creative-Generierung verwendet.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo Upload */}
          <div className="space-y-2">
            <Label htmlFor="brand-logo">Logo</Label>
            {!logoPreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="brand-logo"
                  data-testid="brand-logo-input"
                />
                <label htmlFor="brand-logo">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('brand-logo').click()}
                    data-testid="brand-logo-button"
                    className="cursor-pointer"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Logo hochladen
                  </Button>
                </label>
                <p className="text-xs text-[rgba(245,245,247,0.6)] mt-2">PNG, JPG, JPEG bis 5MB</p>
              </div>
            ) : (
              <div className="border-2 border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <img
                    src={logoPreview}
                    alt="Brand Logo"
                    className="h-20 object-contain"
                    data-testid="brand-logo-preview"
                  />
                  <Button
                    onClick={removeLogo}
                    variant="outline"
                    size="sm"
                    data-testid="remove-brand-logo"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Entfernen
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Colors */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="primary-color">Primärfarbe</Label>
              <div className="flex gap-2">
                <Input
                  id="primary-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-20 h-12"
                  data-testid="primary-color-picker"
                />
                <Input
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#667eea"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-[rgba(245,245,247,0.7)]">Wird für Buttons und Akzente verwendet</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="secondary-color">Sekundärfarbe</Label>
              <div className="flex gap-2">
                <Input
                  id="secondary-color"
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-20 h-12"
                  data-testid="secondary-color-picker"
                />
                <Input
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  placeholder="#764ba2"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-[rgba(245,245,247,0.7)]">Für Gradienten und Highlights</p>
            </div>
          </div>

          {/* Font */}
          <div className="space-y-2">
            <Label htmlFor="brand-font">Standard-Schriftart</Label>
            <Select value={fontFamily} onValueChange={setFontFamily}>
              <SelectTrigger data-testid="brand-font-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DejaVuSans">DejaVu Sans (Modern & Clean)</SelectItem>
                <SelectItem value="DejaVuSerif">DejaVu Serif (Elegant & Seriös)</SelectItem>
                <SelectItem value="DejaVuSansMono">DejaVu Mono (Tech & Bold)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[rgba(245,245,247,0.7)]">Diese Schriftart wird in Ihren Creatives verwendet</p>
          </div>

          {/* Preview */}
          <div className="border-2 border-gray-200 rounded-lg p-6 bg-[rgba(255,255,255,0.03)]">
            <h3 className="text-sm font-semibold text-[rgba(245,245,247,0.9)] mb-3">Vorschau</h3>
            <div className="space-y-3">
              <div 
                className="h-12 rounded flex items-center justify-center text-white font-semibold"
                style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
              >
                Ihr Brand-Gradient
              </div>
              <div className="flex gap-2">
                <div 
                  className="flex-1 h-12 rounded flex items-center justify-center text-white font-semibold"
                  style={{ background: primaryColor }}
                >
                  Primär
                </div>
                <div 
                  className="flex-1 h-12 rounded flex items-center justify-center text-white font-semibold"
                  style={{ background: secondaryColor }}
                >
                  Sekundär
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full h-12 text-lg font-semibold"
            data-testid="save-brand-kit-button"
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
            }}
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Speichert...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Brand-Kit speichern
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="shadow-lg border-2 border-blue-100" data-testid="brand-kit-info">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div>
              <h4 className="font-semibold text-[#F5F5F7] mb-1">Automatische Verwendung</h4>
              <p className="text-sm text-[rgba(245,245,247,0.7)]">
                Nach dem Speichern werden Ihr Logo und Ihre Markenfarben automatisch bei jeder neuen Creative-Generierung verwendet. 
                Sie müssen nichts mehr manuell einstellen!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandKit;
